import {
  Flex,
  FlexItem,
  Form,
  Spinner,
  Title,
  FormSelectOption,
  FormGroup,
  TextInput,
  FormSelect,
} from '@patternfly/react-core'
import { CheckIcon, ErrorCircleOIcon } from '@patternfly/react-icons'
import { useCallback, useMemo, useState } from 'react'
import * as React from 'react'

import {
  useLocations,
  useVisitDefinitions,
} from '@app/domain/visit/visit.hooks'
import type { IVisit } from '@app/domain/visit/visit.interface'
import { useValidatedState } from '@app/lib/hooks/validated-state.hooks'
import { hasContent } from '@app/lib/string'
import { getLocalDate, getLocalTime, getISODateTime } from '@app/lib/time'
import { ValidationStatusId } from '@app/lib/validators'
import type { IVisitStatuses } from '@app/state/config/interfaces'
import * as Visits from '@app/state/visits'
import { SmallButton } from '@app/utils/styles/small-button'

import { ReadOnlyVisitSummaryCard } from './visit-card/read-only-visit-card'

interface IVisitCardProps {
  visit: IVisit
  visitStatuses: IVisitStatuses
  updateVisit: (id: string, visit: Visits.IApiUpdateVisit) => void
  updateStatus?: Visits.VisitActionStatusId
  clearStatus: () => void
}

const VisitSummaryCard: React.FC<IVisitCardProps> = ({
  clearStatus,
  updateStatus,
  updateVisit,
  visit,
}) => {
  const [
    visitType,
    setVisitType,
    visitTypeValidation,
    setVisitTypeValidation,
  ] = useValidatedState(null, ValidationStatusId.Default)
  const [editable, setEditable] = useState(false)
  const [
    visitDate,
    setVisitDate,
    visitDateValidation,
    setVisitDateValidation,
  ] = useValidatedState(null, ValidationStatusId.Default)
  const [
    startTime,
    setStartTime,
    startTimeValidation,
    setStartTimeValidation,
  ] = useValidatedState(null, ValidationStatusId.Default)
  const [
    location,
    setLocation,
    locationValidation,
    setLocationValidation,
  ] = useValidatedState(null, ValidationStatusId.Default)
  const [status, setStatus] = useValidatedState(
    null,
    ValidationStatusId.Default,
  )
  const [isVisitUpdated, setIsVisitUpdated] = useState(false)

  const { locations } = useLocations()
  const { definitions } = useVisitDefinitions(location)

  const EmptyFormSelectOption = useCallback((selected: boolean) => {
    return (
      <FormSelectOption
        key="empty"
        value={null}
        isDisabled
        selected={selected}
        label="Please Choose"
      />
    )
  }, [])

  const generateOptions = (items, valueFn, labelFn) =>
    items?.map((item) => (
      <FormSelectOption
        key={valueFn(item)}
        value={valueFn(item)}
        label={labelFn(item)}
      />
    ))

  const definitionOptions = useMemo(
    () =>
      generateOptions(
        definitions,
        (defn) => defn.id,
        (defn) => defn.name,
      ),
    [definitions],
  )
  const locationOptions = useMemo(
    () =>
      generateOptions(
        locations,
        (location) => location,
        (location) => location,
      ),
    [locations],
  )

  const validateIsNotEmptyOrNull = (newValue: string) => {
    return newValue === '' || newValue === null
      ? ValidationStatusId.Error
      : ValidationStatusId.Success
  }

  const handleSetVisitDate = useCallback(
    (newVisitDate: string) => {
      const status = validateIsNotEmptyOrNull(newVisitDate)
      setVisitDateValidation(status)
      setVisitDate(newVisitDate)
    },
    [setVisitDate, setVisitDateValidation],
  )

  const handleSetStartTime = useCallback(
    (newStartTime: string) => {
      const status = validateIsNotEmptyOrNull(newStartTime)
      setStartTimeValidation(status)
      setStartTime(newStartTime)
    },
    [setStartTime, setStartTimeValidation],
  )

  const handleSetDefinition = useCallback(
    (newDefinition: string | null) => {
      const status = validateIsNotEmptyOrNull(newDefinition)
      setVisitTypeValidation(status)
      setVisitType(newDefinition)
    },
    [setVisitType, setVisitTypeValidation],
  )

  const handleSetLocation = useCallback(
    (newLocation: string | null) => {
      setVisitType(null)
      setVisitTypeValidation(ValidationStatusId.Default)
      const status = validateIsNotEmptyOrNull(newLocation)
      setLocationValidation(status)

      setLocation(newLocation)
    },
    [setVisitType, setVisitTypeValidation, setLocation, setLocationValidation],
  )

  const isFormValid = useCallback(() => {
    return (
      hasContent(visitType) &&
      hasContent(location) &&
      hasContent(visitDate) &&
      hasContent(startTime) &&
      hasContent(status) &&
      Date.parse(getISODateTime(visitDate, startTime))
    )
  }, [visitType, location, startTime, status, visitDate])

  const updatedVisit = useMemo((): Visits.IApiUpdateVisit => {
    return {
      definitionId: visitType,
      location,
      startTime:
        visitDate !== null && startTime !== null
          ? getISODateTime(visitDate, startTime)
          : '',
      visitStatus: status,
    }
  }, [visitType, location, startTime, status, visitDate])

  const handleUpdateVisit = useCallback(() => {
    if (isFormValid()) {
      setIsVisitUpdated(true)
      updateVisit(visit.id, updatedVisit)
      setEditable(false)
      setVisitTypeValidation(ValidationStatusId.Default)
      setLocationValidation(ValidationStatusId.Default)
      setStartTimeValidation(ValidationStatusId.Default)
      setVisitDateValidation(ValidationStatusId.Default)
    }
  }, [
    isFormValid,
    setVisitTypeValidation,
    setLocationValidation,
    setStartTimeValidation,
    setVisitDateValidation,
    updateVisit,
    updatedVisit,
    visit.id,
  ])

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault()
      handleUpdateVisit()
    },
    [handleUpdateVisit],
  )

  const handleSetEditable = useCallback(() => {
    setVisitDate(getLocalDate(visit.startTime))
    setStartTime(getLocalTime(visit.startTime))
    setLocation(visit.location)
    setStatus(visit.status)
    setVisitType(visit.definition.id)
    clearStatus()
    setEditable(true)
  }, [
    clearStatus,
    setVisitType,
    setLocation,
    setStartTime,
    setStatus,
    setVisitDate,
    visit.definition.id,
    visit.location,
    visit.startTime,
    visit.status,
  ])

  const handleCancelEdit = useCallback(() => {
    setVisitDate(getLocalDate(visit.startTime))
    setStartTime(getLocalTime(visit.startTime))
    setLocation(visit.location)
    setStatus(visit.status)
    setVisitType(visit.definition.id)
    setVisitTypeValidation(ValidationStatusId.Default)
    setLocationValidation(ValidationStatusId.Default)
    setStartTimeValidation(ValidationStatusId.Default)
    setVisitDateValidation(ValidationStatusId.Default)
    clearStatus()
    setEditable(false)
  }, [
    clearStatus,
    setVisitType,
    setVisitTypeValidation,
    setLocation,
    setLocationValidation,
    setStartTime,
    setStartTimeValidation,
    setStatus,
    setVisitDate,
    setVisitDateValidation,
    visit.definition.id,
    visit.location,
    visit.startTime,
    visit.status,
  ])

  const renderStatus = () => {
    if (isVisitUpdated) {
      if (updateStatus === Visits.VisitActionStatusId.Pending) {
        return (
          <div>
            <Spinner size="sm" />
          </div>
        )
      } else if (updateStatus === Visits.VisitActionStatusId.Succeeded) {
        return (
          <div>
            <CheckIcon size="sm" color="green" /> Visit updated successfully
          </div>
        )
      } else if (updateStatus === Visits.VisitActionStatusId.Failed) {
        return (
          <div>
            <ErrorCircleOIcon size="sm" color="red" /> Visit update failed
          </div>
        )
      } else {
        return <div />
      }
    } else {
      return <div />
    }
  }

  // TODO: extract editable form into its own component
  const renderEditableForm = () => {
    const validForm = isFormValid()
    return (
      <Flex direction={{ default: 'column' }}>
        <FlexItem>
          <Flex>
            <FlexItem>
              <Title headingLevel="h2" size="lg">
                Visit Information
              </Title>
            </FlexItem>
            <FlexItem>
              <SmallButton variant="secondary" onClick={handleCancelEdit}>
                Cancel
              </SmallButton>
            </FlexItem>
            <FlexItem>
              <SmallButton
                variant="primary"
                isDisabled={!validForm}
                onClick={handleUpdateVisit}
              >
                Save
              </SmallButton>
            </FlexItem>
            <FlexItem>{renderStatus()}</FlexItem>
          </Flex>
        </FlexItem>
        <FlexItem>
          <Form isHorizontal onSubmit={handleSubmit}>
            <FormGroup fieldId="status" label="Status">
              <TextInput
                isDisabled
                type="text"
                id="Status"
                name="Status"
                value={visit.status}
              />
            </FormGroup>
            <FormGroup
              label="Visit Date"
              isRequired
              validated={visitDateValidation}
              helperTextInvalid="Visit Date is required"
              fieldId="visit-date"
            >
              <TextInput
                isRequired
                type="date"
                id="visit-date"
                name="visit-date"
                value={visitDate}
                onChange={handleSetVisitDate}
                validated={visitDateValidation}
              />
            </FormGroup>
            <FormGroup
              label="Start Time"
              isRequired
              fieldId="visit-start-time"
              validated={startTimeValidation}
              helperTextInvalid="Start Time is required"
            >
              <TextInput
                isRequired
                type={'time'}
                id="visit-start-time"
                name="visit-start-time"
                value={startTime}
                onChange={handleSetStartTime}
                validated={startTimeValidation}
              />
            </FormGroup>
            {/* TODO: Create component to reduce the repetition of the form select components */}
            <FormGroup
              fieldId="location"
              label="Location"
              isRequired
              validated={locationValidation}
              helperTextInvalid="Location is required"
            >
              <FormSelect
                value={location}
                isRequired
                onChange={handleSetLocation}
                validated={locationValidation}
              >
                {EmptyFormSelectOption(location === null)}
                {locationOptions}
              </FormSelect>
            </FormGroup>
            <FormGroup
              fieldId="visit-definition"
              label="Visit Type"
              isRequired
              validated={visitTypeValidation}
              helperTextInvalid="Visit Type is required"
            >
              <FormSelect
                value={visitType}
                isRequired
                onChange={handleSetDefinition}
                validated={visitTypeValidation}
              >
                {EmptyFormSelectOption(visitType === null)}
                {definitionOptions}
              </FormSelect>
            </FormGroup>
          </Form>
        </FlexItem>
      </Flex>
    )
  }

  if (editable) {
    return renderEditableForm()
  } else {
    return (
      <ReadOnlyVisitSummaryCard
        isVisitUpdated={isVisitUpdated}
        setEditable={handleSetEditable}
        updateStatus={updateStatus}
        visit={visit}
      />
    )
  }
}

export { VisitSummaryCard }
